import React from "react";
import {
  Box,
  Container,
  Heading,
  SimpleGrid,
  Text,
  VStack,
  useColorModeValue,
  Badge,
  Icon,
} from "@chakra-ui/react";
import { motion } from "framer-motion";
import {
  FaBrain,
  FaStethoscope,
  FaChartLine,
  FaHospital,
  FaShieldAlt,
  FaClock,
  FaCheckCircle,
} from "react-icons/fa";

// Motion components
const MotionBox = motion(Box);
const MotionHeading = motion(Heading);
const MotionText = motion(Text);

// Animations
const fadeInUp = {
  initial: { opacity: 0, y: 30 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.6, ease: "easeOut" }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

interface Feature {
  title: string;
  description: string;
  icon: React.ReactNode | React.ComponentType;
  image?: string;
  color?: string;
  bgColor?: string;
}

interface FeaturesHighlightProps {
  sectionId?: string;
  badgeText?: string;
  title?: string;
  subtitle?: string;
  features?: Feature[];
  maxFeatures?: number;
  columns?: { base: number; lg: number };
  spacing?: number;
  py?: number;
  bg?: string;
}

function FeaturesHighlight({
  sectionId = "features",
  badgeText = "AI-POWERED CLINICAL SOLUTIONS",
  title = "Multi-modal AI assistant for physicians and healthcare professionals",
  subtitle = "Seamlessly integrate AI into your medical practice with our comprehensive platform designed for healthcare excellence",
  features = [],
  maxFeatures = 3,
  columns = { base: 1, lg: 3 },
  spacing = 10,
  py = 20,
  bg,
}: FeaturesHighlightProps) {
  // Medical-first color palette
  const sectionBg = useColorModeValue("gray.50", "gray.900");
  const backgroundColor = bg || sectionBg;
  const cardBg = useColorModeValue("white", "gray.800");
  const borderColor = useColorModeValue("gray.200", "gray.700");
  const textPrimary = useColorModeValue("gray.900", "white");
  const textSecondary = useColorModeValue("gray.600", "gray.300");
  const purplePrimary = "#6366F1";
  const greenSuccess = "#10B981";
  const amberWarning = "#F59E0B";
  const blueInfo = "#3B82F6";

  // Default features if none provided
  const defaultFeatures: Feature[] = [
    {
      title: "AI-Powered Clinical Documentation",
      description: "Transform patient-clinician conversations into comprehensive clinical notes with 99.8% accuracy using advanced medical AI.",
      icon: <FaBrain />,
      color: purplePrimary,
      bgColor: `${purplePrimary}15`,
    },
    {
      title: "Real-time Medical Transcription",
      description: "Capture every detail of patient encounters with real-time transcription that understands medical terminology and context.",
      icon: <FaStethoscope />,
      color: greenSuccess,
      bgColor: `${greenSuccess}15`,
    },
    {
      title: "Intelligent Clinical Analytics",
      description: "Gain insights into patient care patterns and clinical outcomes with AI-driven analytics and predictive modeling.",
      icon: <FaChartLine />,
      color: blueInfo,
      bgColor: `${blueInfo}15`,
    },
  ];

  const displayFeatures = features.length > 0 ? features.slice(0, maxFeatures) : defaultFeatures;

  return (
    <Box as="section" py={py} bg={backgroundColor} id={sectionId}>
      <Container maxW="container.xl">
        <VStack spacing={16}>
          {/* Header Section */}
          <VStack spacing={6} textAlign="center" maxW="container.lg" mx="auto">
            {/* Medical Badge */}
            <MotionBox
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
            >
              <Badge
                px={4}
                py={2}
                bg={`${purplePrimary}15`}
                color={purplePrimary}
                fontSize="sm"
                fontWeight="600"
                borderRadius="full"
                letterSpacing="wide"
                textTransform="uppercase"
                border={`1px solid ${purplePrimary}30`}
              >
                {badgeText}
              </Badge>
            </MotionBox>

            {/* Main Heading */}
            <MotionHeading
              size="2xl"
              lineHeight="1.2"
              fontWeight="700"
              color={textPrimary}
              fontSize={{ base: "3xl", md: "4xl", lg: "5xl" }}
              {...fadeInUp}
            >
              {title}
            </MotionHeading>

            {/* Subtitle */}
            <MotionText
              fontSize={{ base: "lg", md: "xl" }}
              color={textSecondary}
              lineHeight="1.6"
              maxW="800px"
              {...fadeInUp}
              transition={{ delay: 0.2 }}
            >
              {subtitle}
            </MotionText>
          </VStack>

          {/* Features Grid */}
          <MotionBox
            w="full"
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <SimpleGrid columns={columns} spacing={spacing} w="full">
              {displayFeatures.map((feature, idx) => (
                <MotionBox
                  key={idx}
                  variants={fadeInUp}
                  whileHover={{
                    y: -8,
                    transition: { duration: 0.3 },
                  }}
                >
                  <Box
                    bg={cardBg}
                    borderRadius="2xl"
                    boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
                    p={8}
                    border={`1px solid ${borderColor}`}
                    position="relative"
                    overflow="hidden"
                    transition="all 0.3s ease"
                    _hover={{
                      boxShadow: "0 20px 40px rgba(0, 0, 0, 0.12)",
                      borderColor: feature.color || purplePrimary,
                    }}
                  >
                    {/* Background accent */}
                    <Box
                      position="absolute"
                      top={0}
                      left={0}
                      right={0}
                      height="4px"
                      bgGradient={`linear(to-r, ${
                        feature.color || purplePrimary
                      }, ${feature.color || purplePrimary}80)`}
                    />

                    {/* Icon */}
                    <Box
                      mb={6}
                      p={4}
                      borderRadius="xl"
                      bg={feature.bgColor || `${purplePrimary}15`}
                      color={feature.color || purplePrimary}
                      width="fit-content"
                      position="relative"
                    >
                      {React.isValidElement(feature.icon) ? (
                        <Box
                          boxSize={8}
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          {feature.icon}
                        </Box>
                      ) : (
                        <Icon
                          as={feature.icon as React.ComponentType}
                          boxSize={8}
                        />
                      )}
                    </Box>

                    {/* Content */}
                    <VStack spacing={4} align="flex-start">
                      <Heading
                        size="lg"
                        color={textPrimary}
                        fontWeight="700"
                        fontSize={{ base: "xl", md: "2xl" }}
                      >
                        {feature.title}
                      </Heading>
                      <Text
                        fontSize="md"
                        color={textSecondary}
                        lineHeight="1.6"
                      >
                        {feature.description}
                      </Text>
                    </VStack>

                    {/* Hover effect indicator */}
                    <Box
                      position="absolute"
                      bottom={0}
                      left={0}
                      right={0}
                      height="2px"
                      bgGradient={`linear(to-r, ${
                        feature.color || purplePrimary
                      }, ${feature.color || purplePrimary}60)`}
                      transform="scaleX(0)"
                      transition="transform 0.3s ease"
                      _groupHover={{ transform: "scaleX(1)" }}
                    />
                  </Box>
                </MotionBox>
              ))}
            </SimpleGrid>
          </MotionBox>

          {/* Trust Indicators */}
          <MotionBox w="full" {...fadeInUp} transition={{ delay: 0.4 }}>
            <Box
              bg={cardBg}
              borderRadius="2xl"
              p={8}
              border={`1px solid ${borderColor}`}
              boxShadow="0 4px 20px rgba(0, 0, 0, 0.08)"
            >
              <SimpleGrid columns={{ base: 2, md: 4 }} spacing={6}>
                <VStack spacing={2}>
                  <Icon as={FaShieldAlt} boxSize={6} color={greenSuccess} />
                  <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                    Highly Secure
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    Enterprise Security
                  </Text>
                </VStack>
                <VStack spacing={2}>
                  <Icon as={FaCheckCircle} boxSize={6} color={purplePrimary} />
                  <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                    99.8% Accuracy
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    Medical Grade AI
                  </Text>
                </VStack>
                <VStack spacing={2}>
                  <Icon as={FaClock} boxSize={6} color={amberWarning} />
                  <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                    60% Time Saved
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    Clinical Efficiency
                  </Text>
                </VStack>
                <VStack spacing={2}>
                  <Icon as={FaHospital} boxSize={6} color={blueInfo} />
                  <Text fontSize="sm" fontWeight="600" color={textPrimary}>
                    500+ Clinicians
                  </Text>
                  <Text fontSize="xs" color={textSecondary}>
                    Trusted Platform
                  </Text>
                </VStack>
              </SimpleGrid>
            </Box>
          </MotionBox>
        </VStack>
      </Container>
    </Box>
  );
}

export default FeaturesHighlight; 